import React, { useState, useMemo } from "react";
import { 
  Container, 
  Grid, 
  Typography, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem,
  Box,
  TextField,
  Slider,
  Chip,
  Button,
  Pagination,
  InputAdornment,
  Drawer,
  IconButton,
  useMediaQuery,
  Paper,
  alpha,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  Alert,
  Snackbar
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
// Import icons from @mui/icons-material
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import TuneIcon from '@mui/icons-material/Tune';
import PublicIcon from '@mui/icons-material/Public';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// Import coins from your data files
import { australianCoins } from "../components/FeaturedCoins";
import { canadianCoins } from "../pages/CanadaPage";
import { usaCoins } from "../pages/USAPage";
import { ukCoins } from "../pages/UKPage";
import CoinCard from "../components/CoinCard";

// Combine all coins from every country
const allCoins = [...(australianCoins || []), ...(canadianCoins || []), ...(usaCoins || []), ...(ukCoins || [])];

// Process coins to ensure they have all required fields
const processedCoins = allCoins.map(coin => ({
  ...coin,
  country: coin.country || 
    (coin.category === 'australia' ? 'Australia' : 
     coin.category === 'canada' ? 'Canada' : 
     coin.category === 'usa' ? 'USA' : 
     coin.category === 'uk' ? 'UK' : 'Various'),
  era: coin.era || 
    (coin.year ? 
      (parseInt(coin.year) < 1800 ? 'Ancient' :
       parseInt(coin.year) < 1900 ? '19th Century' :
       parseInt(coin.year) < 1950 ? 'Early 20th Century' :
       parseInt(coin.year) < 2000 ? 'Late 20th Century' : 'Modern') : 'Various'),
  metal: coin.metal || 'Various',
  grade: coin.grade || 'Not Graded'
}));

const BuyCoins = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [filters, setFilters] = useState({
    searchQuery: '',
    era: '',
    metal: '',
    country: '',
    priceRange: [0, 100000],
    grade: '',
    year: ''
  });
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [buyDialogOpen, setBuyDialogOpen] = useState(false);
  const [selectedCoin, setSelectedCoin] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    country: '',
    postalCode: '',
    message: '',
    agreeToTerms: false
  });

  const itemsPerPage = 9;

  // Extract unique values from coins data for filters
  const eras = useMemo(() => {
    const uniqueEras = [...new Set(processedCoins.map(coin => coin.era).filter(Boolean))];
    return ['All', ...uniqueEras.sort()];
  }, []);

  const metals = useMemo(() => {
    const uniqueMetals = [...new Set(processedCoins.map(coin => coin.metal).filter(Boolean))];
    return ['All', ...uniqueMetals.sort()];
  }, []);

  const countriesList = useMemo(() => {
    const uniqueCountries = [...new Set(processedCoins.map(coin => coin.country).filter(Boolean))];
    return uniqueCountries.sort();
  }, []);

  const grades = useMemo(() => {
    const uniqueGrades = [...new Set(processedCoins.map(coin => coin.grade).filter(Boolean))];
    // Sort grades properly (Proof comes first, then numeric grades)
    const sorted = uniqueGrades.sort((a, b) => {
      if (a.includes('Proof') && !b.includes('Proof')) return -1;
      if (!a.includes('Proof') && b.includes('Proof')) return 1;
      return a.localeCompare(b);
    });
    return ['All', ...sorted];
  }, []);

  // Filter coins based on all filters
  const filteredCoins = useMemo(() => {
    let result = [...processedCoins];

    // Search query filter
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      result = result.filter(coin => 
        coin.name?.toLowerCase().includes(query) ||
        coin.description?.toLowerCase().includes(query) ||
        coin.year?.toString().includes(query)
      );
    }

    // Era filter
    if (filters.era && filters.era !== 'All') {
      result = result.filter(coin => coin.era === filters.era);
    }

    // Metal filter
    if (filters.metal && filters.metal !== 'All') {
      result = result.filter(coin => coin.metal === filters.metal);
    }

    // Country filter
    if (filters.country && filters.country !== '') {
      result = result.filter(coin => coin.country === filters.country);
    }

    // Grade filter
    if (filters.grade && filters.grade !== 'All') {
      result = result.filter(coin => coin.grade === filters.grade);
    }

    // Year filter
    if (filters.year && filters.year !== '') {
      result = result.filter(coin => coin.year?.toString().includes(filters.year));
    }

    // Price range filter
    result = result.filter(coin => {
      // Extract numeric price from price string if priceNum not available
      let coinPrice = coin.priceNum;
      if (!coinPrice && coin.price) {
        const priceMatch = coin.price.match(/\d[\d,]*/);
        if (priceMatch) {
          coinPrice = parseInt(priceMatch[0].replace(/,/g, ''));
        }
      }
      return coinPrice >= filters.priceRange[0] && coinPrice <= filters.priceRange[1];
    });

    // Sorting
    switch (sortBy) {
      case 'price-asc':
        result.sort((a, b) => (a.priceNum || 0) - (b.priceNum || 0));
        break;
      case 'price-desc':
        result.sort((a, b) => (b.priceNum || 0) - (a.priceNum || 0));
        break;
      case 'grade':
        result.sort((a, b) => {
          const getGradeValue = (grade) => {
            if (grade?.includes('Proof')) return 100;
            const numMatch = grade?.match(/\d+/);
            return numMatch ? parseInt(numMatch[0]) : 0;
          };
          return getGradeValue(b.grade) - getGradeValue(a.grade);
        });
        break;
      case 'newest':
      default:
        result.sort((a, b) => {
          const yearA = parseInt(a.year) || 0;
          const yearB = parseInt(b.year) || 0;
          return yearB - yearA;
        });
        break;
    }

    return result;
  }, [processedCoins, filters, sortBy]);

  const totalPages = Math.ceil(filteredCoins.length / itemsPerPage);
  const paginatedCoins = filteredCoins.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({
      searchQuery: '',
      era: '',
      metal: '',
      country: '',
      priceRange: [0, 100000],
      grade: '',
      year: ''
    });
    setPage(1);
  };

  const handleBuyClick = (coin) => {
    setSelectedCoin(coin);
    setBuyDialogOpen(true);
    setActiveStep(0);
  };

  const handleFormChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmitBuyRequest = async () => {
    // Prepare email content
    const subject = `Purchase Request: ${selectedCoin?.name}`;
    const body = `
      Purchase Request Details:
      
      Coin Information:
      - Name: ${selectedCoin?.name}
      - Year: ${selectedCoin?.year}
      - Grade: ${selectedCoin?.grade}
      - Price: ${selectedCoin?.price}
      - Metal: ${selectedCoin?.metal}
      - Mint: ${selectedCoin?.mint || 'Not specified'}
      
      Buyer Information:
      - Full Name: ${formData.fullName}
      - Email: ${formData.email}
      - Phone: ${formData.phone}
      - Address: ${formData.address}
      - City: ${formData.city}
      - Country: ${formData.country}
      - Postal Code: ${formData.postalCode}
      
      Additional Message:
      ${formData.message}
    `;

    // Create mailto link
    const mailtoLink = `mailto:john.asfar@worldwidecorporation.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    
    // Open default email client
    window.location.href = mailtoLink;
    
    // Show success message
    setSnackbarMessage('Purchase request sent! Our team will contact you within 24 hours.');
    setSnackbarOpen(true);
    
    // Close dialog after 2 seconds
    setTimeout(() => {
      setBuyDialogOpen(false);
      setActiveStep(0);
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        country: '',
        postalCode: '',
        message: '',
        agreeToTerms: false
      });
    }, 2000);
  };

  const handleNext = () => {
    // Validate step 1 form data
    if (activeStep === 1) {
      if (!formData.fullName || !formData.email || !formData.phone || !formData.address || !formData.city || !formData.country) {
        setSnackbarMessage('Please fill in all required fields');
        setSnackbarOpen(true);
        return;
      }
      if (!formData.email.includes('@')) {
        setSnackbarMessage('Please enter a valid email address');
        setSnackbarOpen(true);
        return;
      }
    }
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const activeFilterCount = Object.entries(filters).filter(([key, value]) => {
    if (key === 'priceRange') {
      return value[0] > 0 || value[1] < 100000;
    }
    if (key === 'searchQuery') {
      return value !== '';
    }
    return value !== '' && value !== 'All';
  }).length;

  const FilterContent = () => (
    <Box sx={{ p: isMobile ? 3 : 0 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#2D2D2D' }}>
          <TuneIcon sx={{ color: 'primary.main' }} /> Filters
          {activeFilterCount > 0 && (
            <Chip 
              label={activeFilterCount} 
              size="small" 
              sx={{
                backgroundColor: 'primary.main',
                color: '#FFFFFF',
                fontWeight: 600,
              }}
            />
          )}
        </Typography>
        {isMobile && (
          <IconButton onClick={() => setMobileFilterOpen(false)} sx={{ color: '#616161' }}>
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      {/* Search */}
      <TextField
        fullWidth
        placeholder="Search coins by name, description, or year..."
        variant="outlined"
        size="small"
        value={filters.searchQuery}
        onChange={(e) => handleFilterChange('searchQuery', e.target.value)}
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: 'primary.main' }} />
            </InputAdornment>
          ),
        }}
      />

      {/* Era Filter */}
      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel sx={{ color: '#616161' }}>Era</InputLabel>
        <Select
          value={filters.era || 'All'}
          label="Era"
          onChange={(e) => handleFilterChange('era', e.target.value === 'All' ? '' : e.target.value)}
          sx={{ backgroundColor: '#FFFFFF' }}
        >
          {eras.map(era => (
            <MenuItem key={era} value={era}>{era}</MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Metal Filter */}
      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel sx={{ color: '#616161' }}>Metal</InputLabel>
        <Select
          value={filters.metal || 'All'}
          label="Metal"
          onChange={(e) => handleFilterChange('metal', e.target.value === 'All' ? '' : e.target.value)}
          sx={{ backgroundColor: '#FFFFFF' }}
        >
          {metals.map(metal => (
            <MenuItem key={metal} value={metal}>{metal}</MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Country Filter with Search */}
      <FormControl fullWidth sx={{ mb: 3 }}>
        <Autocomplete
          options={countriesList}
          value={filters.country || null}
          onChange={(event, newValue) => {
            handleFilterChange('country', newValue || '');
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Country"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <PublicIcon sx={{ color: 'primary.main' }} />
                  </InputAdornment>
                ),
              }}
            />
          )}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#FFFFFF',
            },
          }}
        />
      </FormControl>

      {/* Grade Filter */}
      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel sx={{ color: '#616161' }}>Grade</InputLabel>
        <Select
          value={filters.grade || 'All'}
          label="Grade"
          onChange={(e) => handleFilterChange('grade', e.target.value === 'All' ? '' : e.target.value)}
          sx={{ backgroundColor: '#FFFFFF' }}
        >
          {grades.map(grade => (
            <MenuItem key={grade} value={grade}>{grade}</MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Price Range */}
      <Box sx={{ mb: 3 }}>
        <Typography gutterBottom sx={{ color: '#2D2D2D' }}>Price Range</Typography>
        <Slider
          value={filters.priceRange}
          onChange={(e, val) => handleFilterChange('priceRange', val)}
          valueLabelDisplay="auto"
          min={0}
          max={100000}
          step={1000}
          valueLabelFormat={(value) => `$${value.toLocaleString()}`}
          sx={{
            color: 'primary.main',
            '& .MuiSlider-thumb': {
              backgroundColor: 'primary.main',
            },
            '& .MuiSlider-track': {
              backgroundColor: 'primary.main',
            },
            '& .MuiSlider-rail': {
              backgroundColor: alpha('#9E9E9E', 0.3),
            },
          }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="caption" sx={{ color: '#9E9E9E' }}>
            ${filters.priceRange[0].toLocaleString()}
          </Typography>
          <Typography variant="caption" sx={{ color: '#9E9E9E' }}>
            ${filters.priceRange[1].toLocaleString()}
          </Typography>
        </Box>
      </Box>

      {/* Year Input */}
      <TextField
        fullWidth
        label="Year"
        placeholder="e.g., 1907"
        value={filters.year}
        onChange={(e) => handleFilterChange('year', e.target.value)}
        sx={{ mb: 3 }}
      />

      {/* Clear Filters */}
      <Button 
        fullWidth 
        variant="outlined" 
        onClick={clearFilters}
        sx={{ 
          mt: 2,
          borderColor: alpha('#9E9E9E', 0.3),
          color: '#616161',
          '&:hover': {
            borderColor: 'primary.main',
            color: 'primary.main',
            backgroundColor: alpha('#C6A75E', 0.04),
          },
        }}
      >
        Clear All Filters
      </Button>
    </Box>
  );

  // Buy Dialog Steps
  const buySteps = ['Select Coin', 'Your Details', 'Confirm Purchase'];

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ py: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#2D2D2D' }}>
              {selectedCoin?.name}
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={4}>
                <Typography variant="caption" color="#9E9E9E">Year</Typography>
                <Typography variant="body2" sx={{ color: '#2D2D2D', fontWeight: 600 }}>
                  {selectedCoin?.year}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="#9E9E9E">Grade</Typography>
                <Typography variant="body2" sx={{ color: '#2D2D2D', fontWeight: 600 }}>
                  {selectedCoin?.grade}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="#9E9E9E">Price</Typography>
                <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 700 }}>
                  {selectedCoin?.price}
                </Typography>
              </Grid>
            </Grid>
            <Alert severity="info" sx={{ mb: 2 }}>
              Please review the coin details before proceeding with your purchase.
            </Alert>
          </Box>
        );
      case 1:
        return (
          <Box sx={{ py: 2 }}>
            <TextField
              fullWidth
              label="Full Name"
              name="fullName"
              value={formData.fullName}
              onChange={handleFormChange}
              margin="normal"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon sx={{ color: 'primary.main' }} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Email Address"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleFormChange}
              margin="normal"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon sx={{ color: 'primary.main' }} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Phone Number"
              name="phone"
              value={formData.phone}
              onChange={handleFormChange}
              margin="normal"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon sx={{ color: 'primary.main' }} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Address"
              name="address"
              value={formData.address}
              onChange={handleFormChange}
              margin="normal"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocationOnIcon sx={{ color: 'primary.main' }} />
                  </InputAdornment>
                ),
              }}
            />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="City"
                  name="city"
                  value={formData.city}
                  onChange={handleFormChange}
                  margin="normal"
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Postal Code"
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={handleFormChange}
                  margin="normal"
                  required
                />
              </Grid>
            </Grid>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <Autocomplete
                options={countriesList}
                value={formData.country}
                onChange={(event, newValue) => {
                  setFormData({ ...formData, country: newValue || '' });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Country"
                    required
                  />
                )}
              />
            </FormControl>
            <TextField
              fullWidth
              label="Additional Message (Optional)"
              name="message"
              value={formData.message}
              onChange={handleFormChange}
              margin="normal"
              multiline
              rows={3}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DescriptionIcon sx={{ color: 'primary.main' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        );
      case 2:
        return (
          <Box sx={{ py: 2, textAlign: 'center' }}>
            <CheckCircleIcon sx={{ color: 'primary.main', fontSize: 60, mb: 2 }} />
            <Typography variant="h6" gutterBottom sx={{ color: '#2D2D2D' }}>
              Ready to Purchase?
            </Typography>
            <Typography variant="body2" color="#616161" sx={{ mb: 3 }}>
              By confirming, your purchase request will be sent to our team. 
              You will receive a confirmation email with next steps.
            </Typography>
            <Paper sx={{ p: 2, backgroundColor: alpha('#C6A75E', 0.05), mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ color: '#2D2D2D' }}>
                Purchase Summary
              </Typography>
              <Typography variant="body2" color="#616161">
                {selectedCoin?.name} - {selectedCoin?.price}
              </Typography>
            </Paper>
          </Box>
        );
      default:
        return 'Unknown step';
    }
  };

  return (
    <Box sx={{ 
      py: 8,
      backgroundColor: '#F5F5F5',
      minHeight: '100vh',
    }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h2" gutterBottom sx={{ color: '#2D2D2D' }}>
            Acquire
            <Box component="span" sx={{ color: 'primary.main', display: 'block' }}>
              Rare Coins
            </Box>
          </Typography>
          <Typography variant="body1" color="#616161" sx={{ maxWidth: '600px' }}>
            Browse our curated selection of investment-grade coins from Australia, New Zealand, Canada, 
            the United States, and the United Kingdom. Each coin is authenticated and certified by 
            leading numismatic authorities.
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {/* Filters - Desktop */}
          {!isMobile && (
            <Grid item md={3}>
              <Paper 
                sx={{ 
                  p: 3,
                  backgroundColor: '#FFFFFF',
                  border: '1px solid',
                  borderColor: alpha('#9E9E9E', 0.15),
                  boxShadow: '0 4px 12px rgba(0,0,0,0.02)',
                  position: 'sticky',
                  top: 100,
                }}
              >
                <FilterContent />
              </Paper>
            </Grid>
          )}

          {/* Mobile Filter Button */}
          {isMobile && (
            <Grid item xs={12}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<FilterListIcon />}
                onClick={() => setMobileFilterOpen(true)}
                sx={{ 
                  py: 2,
                  borderColor: alpha('#9E9E9E', 0.3),
                  color: '#616161',
                  '&:hover': {
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    backgroundColor: alpha('#C6A75E', 0.04),
                  },
                }}
              >
                Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
              </Button>
            </Grid>
          )}

          {/* Mobile Filter Drawer */}
          <Drawer
            anchor="bottom"
            open={mobileFilterOpen}
            onClose={() => setMobileFilterOpen(false)}
            PaperProps={{
              sx: {
                backgroundColor: '#FFFFFF',
                borderTop: '2px solid',
                borderColor: 'primary.main',
                maxHeight: '90vh',
              }
            }}
          >
            <FilterContent />
          </Drawer>

          {/* Coin Grid */}
          <Grid item xs={12} md={9}>
            {/* Results Count */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4, flexWrap: 'wrap', gap: 2 }}>
              <Typography variant="body1" color="#616161">
                Showing {paginatedCoins.length > 0 ? ((page - 1) * itemsPerPage) + 1 : 0}-
                {Math.min(page * itemsPerPage, filteredCoins.length)} of {filteredCoins.length} results
              </Typography>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <Select 
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  sx={{ 
                    backgroundColor: '#FFFFFF',
                    color: '#2D2D2D',
                  }}
                >
                  <MenuItem value="newest">Newest First</MenuItem>
                  <MenuItem value="price-asc">Price: Low to High</MenuItem>
                  <MenuItem value="price-desc">Price: High to Low</MenuItem>
                  <MenuItem value="grade">Grade: High to Low</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Coins Grid */}
            {paginatedCoins.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography variant="h6" color="#616161">
                  No coins found matching your filters.
                </Typography>
                <Button 
                  variant="outlined" 
                  onClick={clearFilters}
                  sx={{ mt: 2 }}
                >
                  Clear Filters
                </Button>
              </Box>
            ) : (
              <>
                <Grid container spacing={4}>
                  {paginatedCoins.map((coin) => (
                    <Grid item xs={12} sm={6} lg={4} key={coin.id}>
                      <CoinCard 
                        coin={coin} 
                        onPriceInquiry={() => handleBuyClick(coin)}
                      />
                    </Grid>
                  ))}
                </Grid>

                {/* Pagination */}
                {totalPages > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                    <Pagination 
                      count={totalPages} 
                      page={page}
                      onChange={(e, val) => setPage(val)}
                      color="primary"
                      size={isMobile ? "medium" : "large"}
                      sx={{
                        '& .MuiPaginationItem-root': {
                          color: '#616161',
                          '&.Mui-selected': {
                            backgroundColor: 'primary.main',
                            color: '#FFFFFF',
                            '&:hover': {
                              backgroundColor: 'primary.dark',
                            },
                          },
                        },
                      }}
                    />
                  </Box>
                )}
              </>
            )}

            {/* Global Sourcing Visualization */}
            <Paper
              sx={{
                mt: 6,
                p: 3,
                backgroundColor: '#FFFFFF',
                border: '1px solid',
                borderColor: alpha('#9E9E9E', 0.15),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
              }}
            >
              <PublicIcon sx={{ color: 'primary.main', fontSize: 32 }} />
              <Box>
                <Typography variant="body2" sx={{ color: '#2D2D2D', fontWeight: 600 }}>
                  Global Collection
                </Typography>
                <Typography variant="caption" color="#616161">
                  Coins available from {countriesList.length} countries worldwide
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Buy Dialog */}
      <Dialog 
        open={buyDialogOpen} 
        onClose={() => setBuyDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 2
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center' }}>
          <Typography variant="h4" color="primary" gutterBottom>
            Purchase Coin
          </Typography>
          <Stepper activeStep={activeStep} sx={{ mt: 2 }}>
            {buySteps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </DialogTitle>

        <DialogContent>
          {getStepContent(activeStep)}
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'space-between', p: 3 }}>
          <Button
            onClick={() => setBuyDialogOpen(false)}
            variant="outlined"
          >
            Cancel
          </Button>
          <Box>
            {activeStep > 0 && (
              <Button
                onClick={handleBack}
                sx={{ mr: 1 }}
              >
                Back
              </Button>
            )}
            {activeStep === buySteps.length - 1 ? (
              <Button
                onClick={handleSubmitBuyRequest}
                variant="contained"
                startIcon={<ShoppingCartIcon />}
              >
                Confirm Purchase
              </Button>
            ) : (
              <Button
                onClick={handleNext}
                variant="contained"
              >
                Next
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity="success"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BuyCoins;